import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AllServicesService } from '../../../services/all-services.service';
import { SharedServiceService } from '../../services/shared-service.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { startWith, map } from 'rxjs/operators';
import { filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-bc-fob-m-ppd',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ScrollingModule,
    MatFormFieldModule, MatInputModule, MatAutocompleteModule],
  templateUrl: './bc-fob-m-ppd.component.html',
  styleUrl: './bc-fob-m-ppd.component.scss'
})
export class BcFobMPpdComponent implements OnInit, OnChanges {
  @Input() ppdData: any = null; // Receive data from the parent

  // Autocomplete controls
  ppdControls: FormControl[] = [];
  filteredPpdOptions: any[][] = [];
  ppdOptions: any[] = [];

  // Variables to store the last valid selections
  lastValidSelections: any[] = [];

  rows: any[] = [];
  data: any[] = [];
  selectedValue: string = '';

  constructor(private dataService: AllServicesService,
    private sharedService: SharedServiceService
  ) { }

  private formatToTwoDecimals(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2);
  }

  private sanitizeNumericInputValue(value: string): string {
    if (!value) {
      return '';
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) {
      return cleaned;
    }
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }

  restrictNumericInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Tab', 'Enter', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleTreatInput(event: Event, rowIndex: number, field: 'treat1' | 'treat2'): void {
    const target = event.target as HTMLInputElement;
    const cleaned = this.sanitizeNumericInputValue(target.value);
    if (cleaned !== target.value) {
      target.value = cleaned;
    }
    this.rows[rowIndex][field] = cleaned;
    this.limitTreatValue(rowIndex, field);
  }

  onTreatBlur(rowIndex: number, field: 'treat1' | 'treat2'): void {
    const formatted = this.formatToTwoDecimals(this.rows[rowIndex][field]);
    this.rows[rowIndex][field] = formatted;
    this.calculateAndEmitTotals();
  }

  ngOnInit(): void {
    this.addRow();
    this.addRow();

    // Hydrate PPD selections when a favorite is imported
    this.sharedService.ImpFavoriteFlag$
      .pipe(filter(flag => flag === true))
      .subscribe(() => this.hydratePpdFromImport());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ppdData'] && this.ppdData) {
      this.processDropdownData();
    }
  }


  processDropdownData(): void {
    if (this.ppdData) {
      // Transform data for autocomplete
      this.ppdOptions = this.ppdData.map((item: any) => ({
        label: item.Additive,
        value: item.Additive,
        data: item
      })).sort((a: any, b: any) => this.compareByLabel(a, b));

      // Initialize the filtered options for each row
      this.rows.forEach((row, index) => {
        this.filteredPpdOptions[index] = [...this.ppdOptions];
      });
    }
  }

  // Method to filter options based on the entered text
  private _filter(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.ppdOptions.filter(option =>
      option.label.toLowerCase().includes(filterValue)
    ).sort((a: any, b: any) => this.compareByLabel(a, b));
  }

  private compareByLabel(a: any, b: any): number {
    const aLabel = String(a?.label ?? '');
    const bLabel = String(b?.label ?? '');
    return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
  }

  // Method to display the text in the autocomplete
  displayFn(item: any): string {
    return item && item.label ? item.label : '';
  }

  onPpdInputBlur(rowIndex: number): void {
    const currentValue = this.ppdControls[rowIndex].value;
    if (!this.isValidPpdSelection(currentValue)) {
      this.ppdControls[rowIndex].setValue(this.lastValidSelections[rowIndex], { emitEvent: false });
    }
  }

  // Validates whether the current value is a valid selection
  private isValidPpdSelection(value: any): boolean {
    if (!value) return true; // Allow empty values

    // If it is an object with a value property
    if (typeof value === 'object' && value.value) {
      return this.ppdOptions.some(option => option.value === value.value);
    }

    return false; // If it is a string or another type, it is not valid
  }

  // Method for when an option is selected
  onPpdSelected(event: any, rowIndex: number): void {
    const selectedPpd = event.option.value;

    // Save the valid selection
    this.lastValidSelections[rowIndex] = selectedPpd;

    // Update the control
    this.ppdControls[rowIndex].setValue(selectedPpd, { emitEvent: false });

    // Update the row with the selected value
    this.rows[rowIndex].ppd = selectedPpd.value;
    this.rows[rowIndex].KV100cSt = selectedPpd.data.KV100cSt || '';

    this.sharedService.setPpdSelectedData(this.rows);

    // Emit updated data
    this.calculateAndEmitTotals();
  }

  // Method to calculate and emit totals
  calculateAndEmitTotals(): void {
    let totalTreatP1 = 0;
    let totalTreatP2 = 0;

    this.rows.forEach(row => {
      if (row.ppd && row.treat1 && !isNaN(row.treat1)) {
        totalTreatP1 += parseFloat(row.treat1);
      }
      if (row.ppd && row.treat2 && !isNaN(row.treat2)) {
        totalTreatP2 += parseFloat(row.treat2);
      }
    });

    this.sharedService.updateComponentTotals(
      'ppd',
      totalTreatP1,
      totalTreatP2
    );

    this.sharedService.setPpdSelectedData(this.rows);
  }

  limitTreatValue(rowIndex: number, field: 'treat1' | 'treat2'): void {
    const value = this.rows[rowIndex][field];

    if (value && !isNaN(value)) {
      const numValue = parseFloat(value);
      if (numValue > 100) {
        this.rows[rowIndex][field] = '100';
      }
    }

    this.calculateAndEmitTotals();
  }

  addRow() {
    const newIndex = this.rows.length;

    // Create a new control
    const newControl = new FormControl();
    this.ppdControls.push(newControl);

    // Initialize filtered options for this row
    this.filteredPpdOptions[newIndex] = [...this.ppdOptions];

    // Set up the filtering observable
    newControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.label),
      map(name => name ? this._filter(name) : this.ppdOptions.slice())
    ).subscribe(filtered => {
      // Ensure each row has its own filtered options array
      this.filteredPpdOptions[newIndex] = filtered;
    });

    // Add the row
    this.rows.push({
      ppd: '',
      KV100cSt: '',
      treat1: '',
      treat2: ''
    });
  }

  clearRowData(index: number): void {
    this.rows[index].ppd = '';
    this.rows[index].KV100cSt = '';
    this.rows[index].treat1 = '';
    this.rows[index].treat2 = '';

    // Clear the form control as well
    this.ppdControls[index].setValue(null);
    this.lastValidSelections[index] = null;

    // Reset filtered options for this row
    this.filteredPpdOptions[index] = [...this.ppdOptions];

    // Update totals
    this.calculateAndEmitTotals();

    this.sharedService.setPpdSelectedData(this.rows);
  }

  /**
   * Hydrate PPD selections from imported favorite finishedoil_data$.
   * Looks for rows with Fixed_value starting with "PPD".
   */
  private async hydratePpdFromImport(): Promise<void> {
    // Clear existing state before importing
    for (let i = this.rows.length - 1; i >= 0; i--) {
      this.clearRowData(i);
    }

    const foData = await firstValueFrom(this.sharedService.finishedoil_data$);
    if (!foData || !Array.isArray(foData) || foData.length === 0) return;
    if (!this.ppdOptions || this.ppdOptions.length === 0) return;

    const ppdRows = foData.filter((row: any) =>
      row.Fixed_value && row.Fixed_value.startsWith('PPD')
    );

    for (let i = 0; i < ppdRows.length && i < 2; i++) {
      const row = ppdRows[i];
      const ppdName = row.Name;
      if (!ppdName) continue;

      while (this.rows.length <= i) this.addRow();

      const match = this.ppdOptions.find(opt => opt.value === ppdName);
      if (!match) continue;

      this.ppdControls[i].setValue(match, { emitEvent: false });
      this.lastValidSelections[i] = match;
      this.rows[i].ppd = ppdName;
      this.rows[i].KV100cSt = match.data?.KV100cSt || '';
      if (row.Treat_wt_P1) this.rows[i].treat1 = row.Treat_wt_P1;
      if (row.Treat_wt_P2) this.rows[i].treat2 = row.Treat_wt_P2;
    }

    this.calculateAndEmitTotals();
    this.sharedService.setPpdSelectedData(this.rows);
  }
}
